import { useEffect, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { EnvelopeSimple, CheckCircle, Warning } from "@phosphor-icons/react";

// After a voter's email changes: offer to resend the Welcome for each open election.
export default function ResendWelcomeDialog({ voter, initialElections, onClose, onDone }) {
    const [elections, setElections] = useState(initialElections || null);
    const [sending, setSending] = useState(null);
    const [sent, setSent] = useState({});

    useEffect(() => {
        if (!voter || initialElections) return;
        api.get(`/admin/voters/${voter.id}/open-elections`)
            .then(({ data }) => setElections(data.elections))
            .catch(() => setElections([]));
    }, [voter, initialElections]);

    const resend = async (e) => {
        setSending(e.id);
        try {
            await api.post(`/admin/voters/${voter.id}/resend-welcome`, { election_id: e.id });
            setSent((s) => ({ ...s, [e.id]: true }));
            toast.success(`Welcome for "${e.title}" sent to ${voter.email}`);
            onDone?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Could not send");
        }
        setSending(null);
    };

    return (
        <Dialog open={!!voter} onOpenChange={(o) => !o && onClose()}>
            <DialogContent className="sm:max-w-md" data-testid="resend-welcome-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2"><Warning size={18} className="text-amber-600" weight="fill" /> Email address changed</DialogTitle>
                    <DialogDescription data-testid="resend-welcome-desc">
                        Earlier Welcome and Reminder emails went to <span className="font-semibold text-slate-800">{voter?.previous_email || "the old address"}</span>, so {voter?.name || "this voter"} may not have received them. Resend to <span className="font-semibold text-slate-800">{voter?.email}</span>:
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-2" data-testid="resend-welcome-list">
                    {elections === null && <div className="text-xs text-slate-500">Loading open elections…</div>}
                    {elections?.length === 0 && (
                        <div className="text-xs text-slate-500" data-testid="resend-welcome-none">No open elections for this voter right now. They'll be included in the next Welcome you send.</div>
                    )}
                    {elections?.map((e) => (
                        <div key={e.id} className="flex items-center justify-between gap-3 rounded-md border border-slate-200 px-3 py-2">
                            <span className="text-sm font-medium text-slate-900 min-w-0 truncate">{e.title}</span>
                            {sent[e.id] ? (
                                <span className="text-xs text-emerald-700 font-semibold flex items-center gap-1 shrink-0" data-testid={`resend-welcome-sent-${e.id}`}><CheckCircle size={14} weight="fill" /> Sent</span>
                            ) : (
                                <Button size="sm" onClick={() => resend(e)} disabled={!!sending} className="h-8 text-xs bg-civic-700 hover:bg-civic-800 text-white shrink-0" data-testid={`resend-welcome-btn-${e.id}`}>
                                    <EnvelopeSimple size={14} className="mr-1" /> {sending === e.id ? "Sending…" : "Resend Welcome"}
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={onClose} data-testid="resend-welcome-close">Done</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
